// src/modules/app-views/_apps/appmulti/leads/config.js
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import ListProvider from 'components/core/ListProvider';
import DocumentProvider from 'components/core/DocumentProvider';
import { API } from 'constants/Api';
import CardHeaderTitle from 'layouts/Pages/PageHeader/CardHeaderTitle';
import CardHeaderSubtitle from 'layouts/Pages/PageHeader/CardHeaderSubtitle';
import CardHeaderColumn from 'layouts/Pages/PageHeader/CardHeaderColumn';
import CardHeaderIcons from 'layouts/Pages/PageHeader/CardHeaderIcons';

import LeadsInit from './pages/Init';
import leadsInitActionButton from './pages/Init/actions/actionButton';
import leadsInitMenuButton from './pages/Init/actions/menuButton';
import LeadsDocument from './pages/Document';
import leadsDocumentDropdownMenu from './pages/Document/actions/dropdownMenu';
import {
  leadEmailItem,
  leadPhoneItem,
  leadDestinyItem,
  leadDepartureItem,
  leadArrivalItem,
} from './pages/Document/cardHeaderItems';
import seedLeadsParameters from './seed';
import LeadsPreviewHeader from './pages/Init/components/LeadsPreviewHeader';
import LeadsPreviewActions from './pages/Init/components/LeadsPreviewActions';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const MODULE_ID = 'appmultiLeads';

// *******************************************************************************
// PRE CONFIG
// *******************************************************************************
const preConfigChanges = appId => {
  CONFIG.appId = appId;
};

// *******************************************************************************
// MODULE CONFIGURATION
// *******************************************************************************
const CONFIG = {
  version: 2,
  id: MODULE_ID,
  initialPage: 'init',
  initialParams: {},
  initialQuery: {},
  parametersModalComponent: null,
  parametersSeed: seedLeadsParameters,
  migratory: false,
};

// *******************************************************************************
// MODULE PAGES
// *******************************************************************************
const PAGES = {
  init: {
    layout: 'list',
    moduleId: MODULE_ID,
    component: props => (
      <ListProvider {...props}>
        <LeadsInit />
      </ListProvider>
    ),

    tour: {
      firstModal: null,
      lastModal: null,
      onFinish: null,
      triggerConfettiOnFinish: false,
      videoLink: { br: null, us: null },
    },

    api: {
      url: API.APPMULTI_LEADS.PAGINATION,
      params: null,
      query: null,
      returnAsObject: false,
      autoFetch: true,
      fetchWithoutSubscription: false,
      removeSubscriptionIdFromHeader: false,
      persistInStore: true,
    },

    decorators: { request: null, response: null },

    routing: { endpoint: null, paramKey: null, query: null },

    pagination: {
      pagination: { page: 1, size: 10 },
      sort: [{ field: '_metadata.audit.updatedAt', order: 'desc', index: 1 }],
      search: {},
      textSearch: '',
      filter: {},
      drawer: { folder: 'inbox' },
    },

    table: {
      actions: ['edit', 'archive', 'remove'],
      hiddenColumns: ['createdAt'],
    },

    settings: null,

    preview: {
      actions: ['edit', 'archive', 'remove'],
      showCardHeader: false,
      noDividerAfterHeader: true,
      components: {
        title: 'lead',
        header: props => <LeadsPreviewHeader {...props} />,
        body: null,
        actions: props => <LeadsPreviewActions {...props} />,
        footer: null,
      },
    },

    header: {
      visible: true,
      style: 'default',
      // Golden contract: Init actions WITHOUT () — viewer-only (no new button)
      actions: ['filter', 'refresh', leadsInitActionButton, leadsInitMenuButton],
      navigation: {
        showTitle: true,
        showBreadcrumb: true,
        customBreadcrumb: () => ['commercial', 'leads'],
      },
    },
  },

  document: {
    highSecurity: false,

    layout: 'document',
    moduleId: MODULE_ID,
    component: props => (
      <DocumentProvider {...props}>
        <LeadsDocument />
      </DocumentProvider>
    ),

    tour: {
      firstModal: null,
      lastModal: null,
      onFinish: null,
      triggerConfettiOnFinish: false,
      videoLink: { br: null, us: null },
    },

    api: {
      url: API.APPMULTI_LEADS.URL,
      params: null,
      query: null,
      returnAsObject: true,
      autoFetch: true,
      fetchWithoutSubscription: false,
      removeSubscriptionIdFromHeader: false,
      persistInStore: true,
    },

    decorators: { request: null, response: null },

    routing: { endpoint: '/:_id', paramKey: '_id', query: null },

    pagination: null,
    table: null,

    settings: {
      initialTab: 'quotations',
      initialValues: {},
      // Golden contract: Document actions WITH () — no save/edit/new
      actions: [
        'editRefresh',
        leadsDocumentDropdownMenu(),
        'return',
      ],
      viewMode: 'view',
    },

    header: {
      visible: true,
      style: 'card',
      avatar: { visible: true, placeholder: 'file-text' },
      navigation: {
        showTitle: true,
        showBreadcrumb: true,
        customBreadcrumb: () => ['commercial', 'leads'],
      },
      card: {
        title: props => <CardHeaderTitle {...props} placeholder="lead" />,
        subtitle: props => <CardHeaderSubtitle {...props} />,
        column1: props => (
          <CardHeaderColumn
            {...props}
            items={[leadEmailItem, leadPhoneItem]}
          />
        ),
        column2: props => (
          <CardHeaderColumn
            {...props}
            items={[leadDestinyItem, leadDepartureItem, leadArrivalItem]}
          />
        ),
        icons: (data, countryCode) => (
          <CardHeaderIcons data={data} countryCode={countryCode} />
        ),
      },
    },
  },
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default { CONFIG, PAGES, preConfigChanges };
